import React, { useMemo, useState, useCallback } from 'react'
import {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	Column,
} from '@tanstack/react-table'
import {
	Edit,
	Delete,
	FormatListBulletedOutlined,
	AutoAwesomeRounded,
	KeyboardArrowDown,
	KeyboardArrowUp,
	UnfoldMore,
	PlaylistAddRounded,
	RadioButtonChecked,
	RadioButtonUnchecked,
} from '@mui/icons-material'
import { IconButton, Tooltip } from '../../components/ui'
import { useUserDateFormat } from '../useUserDateFormat'
import cn from '../../lib/utils/cn'
import { OrganizationCategoriesItem, OrganizationUser } from '../../lib/api/types/organization.interfaces'

export type AdminCategoriesItemType = 'category' | 'subcategory' | 'field'

export const getCategoriesItemIcon = (
	type: AdminCategoriesItemType,
	isChecked?: boolean,
	isAiGenerated?: boolean,
	isOpened?: boolean,
	hasSubcategories?: boolean
) => {
	const iconClass = isChecked ? 'fill-accent' : 'fill-text-secondary'
	const disabledIconClass = 'fill-text-disabled opacity-50'

	switch (type) {
		case 'category':
			if (!hasSubcategories) {
				return <KeyboardArrowDown className={disabledIconClass} />
			}
			return isOpened ? (
				<KeyboardArrowUp className={iconClass} />
			) : (
				<KeyboardArrowDown className={iconClass} />
			)
		case 'subcategory':
			return null
		case 'field':
			return isAiGenerated ? (
				<AutoAwesomeRounded className={iconClass} />
			) : (
				<FormatListBulletedOutlined className={iconClass} />
			)
		default:
			return null
	}
}

export interface AdminCategoriesItem {
	id: string
	name: string
	modifiedAt?: Date | string
	modifiedBy?: OrganizationUser
	isDefault: boolean
	type?: AdminCategoriesItemType
	level?: number
	isAiGenerated?: boolean
	hasSubcategories?: boolean
	originalCategory?: any
	originalField?: any
	[key: string]: any
}

export type AdminCategoriesDataTableEntry = AdminCategoriesItem & {
	options: {
		id: string
	}
}

interface useCreateAdminCategoriesTableProps<T extends AdminCategoriesItem> {
	data?: T[] | null
	searchQuery?: string
	limit?: number
	offset?: number
	isMobile?: boolean
	openedCategoryId?: string | null
	onEdit?: (item: T) => void
	onDelete?: (item: T) => void
	onRowClick?: (item: T) => void
	onCreateSubcategory?: (category: T) => void
	onSetAsDefault?: (category: T) => void
	onShowAiInfo?: (category: OrganizationCategoriesItem) => void
	customColumns?: ColumnDef<any>[]
}

export const renderDefaultBadge = (value: boolean, isSubcategory?: boolean) => {
	if (!value) {
		return null
	}
	return (
		<Tooltip
			label={`This ${isSubcategory ? 'subcategory' : 'category'
				} will be applied by default for new documents`}
			className="w-[165px]"
		>
			<div className="bg-background-light-hover/50 hover:bg-white dark:bg-background-light-hover-dark text-text-main dark:text-text-main-dark hover:text-accent dark:hover:text-accent duration-default text-[10px] flex items-center justify-center px-1 rounded font-medium">
				default
			</div>
		</Tooltip>
	)
}

export const useCreateAdminCategoriesDataTable = <
	T extends AdminCategoriesItem
>({
	data = [],
	searchQuery = '',
	limit = 10,
	offset = 0,
	isMobile,
	openedCategoryId,
	onEdit,
	onDelete,
	onRowClick,
	onSetAsDefault,
	onShowAiInfo,
	onCreateSubcategory,
	customColumns,
}: useCreateAdminCategoriesTableProps<T>) => {
	const { formatDate, formatDateTime } = useUserDateFormat()
	const [sorting, setSorting] = useState<{
		orderBy: string
		orderDirection: 'asc' | 'desc' | false
	}>({
		orderBy: '',
		orderDirection: false,
	})

	const toggleSorting = useCallback((columnId: string) => {
		setSorting((prev) => {
			if (prev.orderBy === columnId) {
				if (prev.orderDirection === 'asc') {
					return { orderBy: columnId, orderDirection: 'desc' }
				}
				if (prev.orderDirection === 'desc') {
					return { orderBy: '', orderDirection: false }
				}
			}
			return { orderBy: columnId, orderDirection: 'asc' }
		})
	}, [])

	const sortControllers = {
		orderBy: sorting.orderBy,
		orderDirection: sorting.orderDirection,
		toggleSorting,
	}

	const tableData: AdminCategoriesDataTableEntry[] = useMemo(() => {
		return (data ?? []).map((item) => ({
			...item,
			options: {
				id: item.id,
			},
		}))
	}, [data])

	const renderHeaderItem = useCallback(
		(
			title: string,
			column: Column<AdminCategoriesDataTableEntry, unknown>,
			className?: string
		) => {
			return (
				<div
					className={cn(
						'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 dark:text-text-table-dark',
						className
					)}
					onClick={() => {
						sortControllers?.toggleSorting(column?.id as string)
					}}
				>
					<span className="text-center w-fit">{title}</span>
					<span
						className={cn(
							'flex items-center justify-center transition-all duration-200 w-3 h-5',
							!column.getIsSorted() && 'hidden group-hover:inline-block'
						)}
					>
						{column.getIsSorted() === 'asc' && (
							<KeyboardArrowUp className="w-5 h-5" />
						)}
						{column.getIsSorted() === 'desc' && (
							<KeyboardArrowDown className="w-5 h-5" />
						)}
						{!column.getIsSorted() && (
							<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
						)}
					</span>
				</div>
			)
		},
		[]
	)

	const columns = useMemo<ColumnDef<any>[]>(() => {
		if (customColumns) {
			return customColumns
		}

		const base: ColumnDef<any>[] = [
			{
				accessorKey: 'name',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 140,
				maxSize: 500,
				size: isMobile ? 170 : 200,
				header: ({ column }) => renderHeaderItem('Name', column),
				cell: ({ getValue, row }) => {
					const icon = row.original.type
						? getCategoriesItemIcon(
							row.original.type,
							false,
							row.original.isAiGenerated,
							row.original.type === 'category' &&
							openedCategoryId === row.original.id,
							row.original.hasSubcategories
						)
						: null
					const level = row.original.level || 0
					const paddingLeft = level * 20

					return (
						<div
							className="flex items-center gap-2 lg:gap-3"
							style={{ paddingLeft: `${paddingLeft}px` }}
						>
							{icon}
							<span className="dark:text-text-table-dark">
								{String(getValue() ?? '')}
							</span>
							{renderDefaultBadge(row?.original?.isDefault)}
						</div>
					)
				},
			},
			{
				accessorKey: 'modifiedAt',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 100,
				maxSize: 250,
				size: 150,
				header: ({ column }) => renderHeaderItem('Modified Date', column),
				cell: ({ getValue }) => {
					const value = getValue() as Date | string | undefined
					return (
						value ? (
							<Tooltip label={formatDateTime(String(value))} classNameChildrenWrapper="w-fit">
								<p className="text-start dark:text-text-table-dark">
									{formatDate(String(value))}
								</p>
							</Tooltip>
						) : (
							<p className="text-start dark:text-text-table-dark">-</p>
						)
					)
				},
			},
			{
				accessorKey: 'modifiedBy',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 100,
				maxSize: 250,
				size: 150,
				header: ({ column }) => renderHeaderItem('Modifier', column),
				cell: ({ getValue }) => {
					const modifiedBy = getValue() as OrganizationUser | undefined
					const name = modifiedBy?.name
					const email = modifiedBy?.email
					const displayText = name || email
					const tooltipText = name && email ? `${name} (${email})` : displayText
					return displayText ? (
						<Tooltip label={tooltipText} classNameChildrenWrapper="w-fit">
							<p className="truncate dark:text-text-table-dark">
								{displayText}
							</p>
						</Tooltip>
					) : (
						<p className="text-start dark:text-text-table-dark">-</p>
					)
				},
			},
			{
				accessorKey: 'options',
				enableSorting: false,
				enableResizing: false,
				size: isMobile ? 100 : 150,
				header: '',
				cell: ({ row }) => (
					<div className="flex items-center justify-end gap-2 md:gap-3 lg:gap-3 p-2 lg:opacity-0 opacity-100 group-hover:opacity-100 duration-default">
						{!row.original?.isDefault && (
							<IconButton
								variant="secondary"
								icon={
									<>
										<RadioButtonUnchecked className="absolute fill-text-main dark:fill-text-main-dark opacity-100 group-hover/button:opacity-0 duration-default" />
										<RadioButtonChecked className="absolute fill-accent opacity-0 group-hover/button:opacity-100 duration-default" />
									</>
								}
								onClick={() => onSetAsDefault?.(row.original)}
								tooltip="Set as default"
								className="p-0 min-w-8 w-8 h-8 dark:text-text-main-dark group/button relative"
							/>
						)}
						{!!onShowAiInfo && (
							<IconButton
								variant="secondary"
								icon={
									<AutoAwesomeRounded className="fill-text-main dark:fill-text-main-dark" />
								}
								onClick={() => onShowAiInfo(row.original)}
								tooltip="Check AI's prompts"
								className="min-w-8 w-8 h-8 dark:text-text-main-dark"
							/>
						)}
						{row.original.type === 'category' && !!onCreateSubcategory && (
							<IconButton
								variant="secondary"
								icon={
									<PlaylistAddRounded className="fill-text-main dark:fill-text-main-dark" />
								}
								onClick={() =>
									onCreateSubcategory(row.original as unknown as T)
								}
								tooltip="Create Subcategory"
								className="min-w-8 w-8 h-8 dark:text-text-main-dark"
							/>
						)}
						{!!onEdit && (
							<IconButton
								variant="secondary"
								icon={
									<Edit className="fill-text-main dark:fill-text-main-dark" />
								}
								onClick={() => onEdit(row.original as unknown as T)}
								tooltip="Edit"
								className="min-w-8 w-8 h-8 dark:text-text-main-dark"
							/>
						)}
						{!!onDelete && (
							<IconButton
								variant="secondary"
								icon={
									<Delete className="fill-text-main dark:fill-text-main-dark" />
								}
								onClick={() => onDelete(row.original as unknown as T)}
								tooltip="Delete"
								className="min-w-8 w-8 h-8 dark:text-text-main-dark"
							/>
						)}
					</div>
				),
			},
		]

		if (isMobile) {
			return base.filter((columnDef) => {
				const accessorKey = (columnDef as any)?.accessorKey as string
				return accessorKey && ['id', 'name', 'options'].includes(accessorKey)
			})
		}

		return base
	}, [
		onEdit,
		onDelete,
		onCreateSubcategory,
		renderHeaderItem,
		isMobile,
		customColumns,
		openedCategoryId,
	])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		enableSorting: true,
		state: {
			globalFilter: searchQuery,
			pagination: {
				pageSize: limit,
				pageIndex: offset / limit,
			},
		},
		globalFilterFn: (row, columnId, filterValue) => {
			const searchValue = String(filterValue).toLowerCase()
			const name = String(row.original.name ?? '').toLowerCase()
			return name.includes(searchValue)
		},
	})

	return {
		table,
	}
}

// create fields table hook

export interface AdminCategoryField {
	id: string
	name: string
	friendlyName?: string
	type: string
	length?: number
	aiGenerated?: boolean
	modifiedAt?: Date | string
	level?: number
	originalField?: any
	[key: string]: any
}

export type AdminCategoryFieldDataTableEntry = AdminCategoryField & {
	options: {
		id: string
	}
}

interface useCreateAdminCategoryFieldsDataTableProps {
	data?: AdminCategoryField[] | null
	searchQuery?: string
	limit?: number
	offset?: number
	isMobile?: boolean
	onEdit?: (field: AdminCategoryField) => void
	onDelete?: (field: AdminCategoryField) => void
	onShowAiInfo?: (field: AdminCategoryField) => void
}

export const useCreateAdminCategoryFieldsDataTable = ({
	data = [],
	searchQuery = '',
	limit = 10,
	offset = 0,
	isMobile,
	onEdit,
	onDelete,
	onShowAiInfo,
}: useCreateAdminCategoryFieldsDataTableProps) => {
	const { formatDate, formatDateTime } = useUserDateFormat()
	const [sorting, setSorting] = useState<{
		orderBy: string
		orderDirection: 'asc' | 'desc' | false
	}>({
		orderBy: '',
		orderDirection: false,
	})

	const toggleSorting = useCallback((columnId: string) => {
		setSorting((prev) => {
			if (prev.orderBy === columnId) {
				if (prev.orderDirection === 'asc') {
					return { orderBy: columnId, orderDirection: 'desc' }
				} else if (prev.orderDirection === 'desc') {
					return { orderBy: '', orderDirection: false }
				}
			}
			return { orderBy: columnId, orderDirection: 'asc' }
		})
	}, [])

	const sortControllers = {
		orderBy: sorting.orderBy,
		orderDirection: sorting.orderDirection,
		toggleSorting,
	}

	const tableData: AdminCategoryFieldDataTableEntry[] = useMemo(() => {
		return (data ?? []).map((field) => ({
			...field,
			options: {
				id: field.id,
			},
		}))
	}, [data])

	const renderHeaderItem = useCallback(
		(
			title: string,
			column: Column<AdminCategoryFieldDataTableEntry, unknown>,
			className?: string
		) => {
			return (
				<div
					className={cn(
						'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 dark:text-text-table-dark',
						className
					)}
					onClick={() => {
						sortControllers?.toggleSorting(column?.id as string)
					}}
				>
					<span className="text-center w-fit">{title}</span>
					<span
						className={cn(
							'flex items-center justify-center transition-all duration-200 w-3 h-5',
							!column.getIsSorted() && 'hidden group-hover:inline-block'
						)}
					>
						{column.getIsSorted() === 'asc' && (
							<KeyboardArrowUp className="w-5 h-5" />
						)}
						{column.getIsSorted() === 'desc' && (
							<KeyboardArrowDown className="w-5 h-5" />
						)}
						{!column.getIsSorted() && (
							<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
						)}
					</span>
				</div>
			)
		},
		[]
	)

	const columns = useMemo<ColumnDef<AdminCategoryFieldDataTableEntry>[]>(() => {
		const base: ColumnDef<AdminCategoryFieldDataTableEntry>[] = [
			{
				accessorKey: 'name',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 140,
				maxSize: 500,
				size: isMobile ? 150 : 300,
				header: ({ column }) => renderHeaderItem('Name', column),
				cell: ({ getValue, row }) => {
					const icon = getCategoriesItemIcon(
						'field',
						false,
						row.original.aiGenerated
					)

					return (
						<div className="flex items-center gap-2 lg:gap-3">
							{icon}
							<span className="dark:text-text-table-dark">
								{String(getValue() ?? '')}
							</span>
						</div>
					)
				},
			},
			{
				accessorKey: 'type',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 100,
				maxSize: 300,
				size: 120,
				header: ({ column }) => renderHeaderItem('Type', column),
				cell: ({ getValue }) => (
					<p className="text-start dark:text-text-table-dark">
						{String(getValue() ?? '-')}
					</p>
				),
			},
			{
				accessorKey: 'length',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 80,
				maxSize: 200,
				size: 80,
				header: ({ column }) => renderHeaderItem('Length', column),
				cell: ({ getValue }) => (
					<p className="text-start dark:text-text-table-dark">
						{String(getValue() ?? '-')}
					</p>
				),
			},
			{
				accessorKey: 'modifiedAt',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 100,
				maxSize: 200,
				size: 100,
				header: ({ column }) => renderHeaderItem('Modified Date', column),
				cell: ({ getValue }) => {
					const value = getValue() as Date | string | undefined
					return (
						value ? (
							<Tooltip label={formatDateTime(String(value))} classNameChildrenWrapper="w-fit">
								<p className="text-start dark:text-text-table-dark">
									{formatDate(String(value))}
								</p>
							</Tooltip>
						) : (
							<p className="text-start dark:text-text-table-dark">-</p>
						)
					)
				},
			},
			{
				accessorKey: 'options',
				enableSorting: false,
				enableResizing: false,
				size: isMobile ? 100 : 150,
				header: '',
				cell: ({ row }) => (
					<div className="flex items-center justify-end gap-2 md:gap-3 lg:gap-4 p-2 lg:opacity-0 opacity-100 group-hover:opacity-100 duration-default">
						{row.original.aiGenerated && onShowAiInfo && (
							<IconButton
								variant="secondary"
								icon={
									<AutoAwesomeRounded className="fill-text-main dark:fill-text-main-dark" />
								}
								onClick={() => onShowAiInfo(row.original)}
								tooltip="Check AI's prompts"
								className="w-8 h-8 dark:text-text-main-dark"
							/>
						)}
						{onEdit && (
							<IconButton
								variant="secondary"
								icon={
									<Edit className="fill-text-main dark:fill-text-main-dark" />
								}
								onClick={() => onEdit(row.original)}
								tooltip="Edit"
								className="w-8 h-8 dark:text-text-main-dark"
							/>
						)}
						{onDelete && (
							<IconButton
								variant="secondary"
								icon={
									<Delete className="fill-text-main dark:fill-text-main-dark" />
								}
								onClick={() => onDelete(row.original)}
								tooltip="Delete"
								className="w-8 h-8 dark:text-text-main-dark"
							/>
						)}
					</div>
				),
			},
		]

		if (isMobile) {
			return base.filter((columnDef) => {
				const accessorKey = (columnDef as any)?.accessorKey as string
				return accessorKey && ['id', 'name', 'options'].includes(accessorKey)
			})
		}

		return base
	}, [onEdit, onDelete, renderHeaderItem, isMobile])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		enableSorting: true,
		state: {
			globalFilter: searchQuery,
			pagination: {
				pageSize: limit,
				pageIndex: offset / limit,
			},
		},
		globalFilterFn: (row, columnId, filterValue) => {
			const searchValue = String(filterValue).toLowerCase()
			const name = String(row.original.name ?? '').toLowerCase()
			const type = String(row.original.type ?? '').toLowerCase()
			return name.includes(searchValue) || type.includes(searchValue)
		},
	})

	return {
		table,
	}
}
